import Formatter from "../Formatter";

const BLOCKS_IN_CYCLE = 210_000;
const BLOCKS_PER_DAY = 144; // ~10 min per block
const BLOCKS_PER_YEAR = 365 * BLOCKS_PER_DAY; // 52,560
const INITIAL_BLOCK_REWARD = 50;
const MAX_SUPPLY = 20999999.9769;
const GENESIS_BLOCK_DATE = "01/3/2009";

export default class BlockCalculator {
  // --- Halving / cycles ------------------------------------------------------

  public static calculateNextHalvingBlock(currentBlock: number): number {
    return Math.ceil(currentBlock / BLOCKS_IN_CYCLE) * BLOCKS_IN_CYCLE;
  }

  public static calculateBlocksLeftToHalving(currentBlock: number): number {
    return this.calculateNextHalvingBlock(currentBlock) - currentBlock;
  }

  public static calculateCycleProgress(currentBlock: number): string {
    return (
      (
        100 -
        (this.calculateBlocksLeftToHalving(currentBlock) / BLOCKS_IN_CYCLE) * 100
      ).toFixed(1) + "%"
    );
  }

  public static estimateTimeToNextHalving(currentBlock: number): string {
    const numberOfBlocksLeft = this.calculateNumberOfBlocksLeft(currentBlock);
    const timeLeftInMs = numberOfBlocksLeft * 10 * 60 * 1000;
    return Formatter.getDays(timeLeftInMs);
  }

  public static estimateNextHalvingDate(currentBlock: number): string {
    const blocksLeft = this.calculateNumberOfBlocksLeft(currentBlock);
    const daysLeft = (blocksLeft * 10) / 60 / 24;

    const currentDate = new Date();
    const estimatedDate = new Date(
      currentDate.setDate(currentDate.getDate() + daysLeft),
    );

    return estimatedDate.toLocaleString("default", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  // --- Reward / issuance -----------------------------------------------------

  public static getCurrentBlockReward(currentBlock: number): number {
    const halvings = Math.floor(currentBlock / BLOCKS_IN_CYCLE);
    const reward = INITIAL_BLOCK_REWARD / Math.pow(2, halvings);
    return reward > 0 ? reward : 0;
  }

  public static calculateDailyIssuance(currentBlock: number): number {
    return this.getCurrentBlockReward(currentBlock) * BLOCKS_PER_DAY;
  }

  public static calculateAnnualIssuance(currentBlock: number): number {
    return this.getCurrentBlockReward(currentBlock) * BLOCKS_PER_YEAR;
  }

  public static calculateAnnualInflationRate(
    currentBlock: number,
    currentSupply?: number,
  ): number {
    const supply =
      currentSupply ?? this.calculateCurrentEmission(currentBlock);

    if (supply <= 0) return 0;

    const annualIssuance = this.calculateAnnualIssuance(currentBlock);
    return (annualIssuance / supply) * 100; // %
  }

  // --- Supply / emission -----------------------------------------------------

  public static calculateCurrentEmission(currentBlock: number): number {
    const currentCycle = Math.ceil(currentBlock / BLOCKS_IN_CYCLE);

    let availableSupply = 0;

    for (let i = 1; i <= currentCycle; i++) {
      const reward = INITIAL_BLOCK_REWARD / Math.pow(2, i - 1);
      const cycleStartBlock = (i - 1) * BLOCKS_IN_CYCLE;
      const cycleEndBlock = i * BLOCKS_IN_CYCLE;

      const isCompletedCycle = cycleEndBlock < currentBlock;

      if (isCompletedCycle) {
        availableSupply += BLOCKS_IN_CYCLE * reward;
      } else {
        const blocksInCurrentCycle = currentBlock - cycleStartBlock;
        availableSupply += Math.max(blocksInCurrentCycle, 0) * reward;
      }
    }

    return Math.min(availableSupply, MAX_SUPPLY);
  }

  public static calculateAvailableSupplyPercentage(
    currentBlock: number,
  ): number {
    const currentEmission = this.calculateCurrentEmission(currentBlock);
    return (currentEmission / MAX_SUPPLY) * 100;
  }

  // --- Network age -----------------------------------------------------------

  public static calculateNetworkAgeInDays(): number {
    const genesisBlockDate = new Date(GENESIS_BLOCK_DATE);
    const currentDate = new Date();

    const differenceInTime =
      currentDate.getTime() - genesisBlockDate.getTime();
    const differenceInDays = differenceInTime / (1000 * 3600 * 24);
    return Math.trunc(differenceInDays);
  }

  public static calculateNetworkAgeInYears(): number {
    return Math.floor(this.calculateNetworkAgeInDays() / 365.25);
  }

  public static calculateAverageBlockTimeInMinutes(currentBlock: number): number {
    if (currentBlock <= 0) return 0;

    const totalMinutesSinceGenesis = this.calculateNetworkAgeInDays() * 24 * 60;
    return totalMinutesSinceGenesis / currentBlock;
  }

  public static calculateAverageBlockTime(currentBlock: number): string {
    const averageBlockTimeInMinutes =
      this.calculateAverageBlockTimeInMinutes(currentBlock);
    const totalSeconds = Math.round(averageBlockTimeInMinutes * 60);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    return `${minutes}m ${seconds}s`;
  }

  // --- Helpers ---------------------------------------------------------------

  private static calculateNumberOfBlocksLeft(currentBlock: number): number {
    return this.calculateNextHalvingBlock(currentBlock) - currentBlock;
  }
}
